import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import apiClient from '../services/apiService';
import { useAuth } from './AuthContext';

export type Extent = 'view' | 'view+modify' | 'view+modify+approve';

const EXTENT_LEVELS: Record<Extent, number> = {
  view: 1,
  'view+modify': 2,
  'view+modify+approve': 3,
};

// ── Workflow action engine (mirrors backend utils/permissions.py) ──────────
export type WorkflowAction =
  | 'view' | 'report' | 'create' | 'edit' | 'delete'
  | 'submit' | 'approve' | 'reject' | 'resubmit' | 'deactivate';

type OwnerRule = null | 'creator' | 'approver' | 'state' | 'not_creator';
const PRE_APPROVAL = new Set(['draft', 'submitted', 'rejected']);

interface ActionRule {
  extent: Extent;
  states: Set<string> | null; // null = state-agnostic
  owner: OwnerRule;
}

const ACTION_RULES: Record<WorkflowAction, ActionRule> = {
  view:       { extent: 'view',                states: null,                                              owner: null },
  report:     { extent: 'view',                states: null,                                              owner: null },
  create:     { extent: 'view+modify',         states: null,                                              owner: null },
  edit:       { extent: 'view+modify',         states: new Set(['draft', 'submitted', 'rejected', 'approved']), owner: 'state' },
  delete:     { extent: 'view+modify',         states: new Set(['draft']),                                owner: 'creator' },
  // Workflow transitions are ownership-free (product decision 2026-07-03): any
  // extent holder may submit/resubmit, not just the creator. Mirrors backend.
  submit:     { extent: 'view+modify',         states: new Set(['draft']),                                owner: null },
  approve:    { extent: 'view+modify+approve', states: new Set(['submitted']),                            owner: null },
  reject:     { extent: 'view+modify+approve', states: new Set(['submitted']),                            owner: null },
  resubmit:   { extent: 'view+modify',         states: new Set(['rejected']),                             owner: null },
  deactivate: { extent: 'view+modify+approve', states: new Set(['approved']),                             owner: 'approver' },
};

/** A record the action engine reasons over. */
export interface RecordLike {
  id?: number;
  workflow_state?: string | null;
  created_by?: number | null;
  approved_by?: number | null;
}

export interface Permission {
  activity_id: number;
  activity_key: string | null;
  activity_name: string;
  module_code: string | null;
  extent: Extent;
  level: number;
}

export interface PermissionResult {
  allowed: boolean;
  message: string;
  extent?: Extent | null;
}

interface PermissionContextType {
  loading: boolean;
  isSuperAdmin: boolean;
  userId: number | null;
  permissions: Permission[];
  /** Extent-only check (RBAC layer). */
  check: (activity: string | number, required: Extent) => PermissionResult;
  /** Full engine: action × state × ownership for one record. */
  can: (activityKey: string | number, action: WorkflowAction, record?: RecordLike | null) => PermissionResult;
  refresh: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

const levelOf = (e?: string | null) => (e && (e in EXTENT_LEVELS) ? EXTENT_LEVELS[e as Extent] : 0);

const matchActivity = (perms: Permission[], activity: string | number): Permission | undefined =>
  perms.find((p) =>
    typeof activity === 'number'
      ? p.activity_id === activity
      : (p.activity_key || '') === activity || (p.activity_name || '').toLowerCase() === String(activity).toLowerCase(),
  );

export const PermissionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { state } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const userId = state.user?.id ?? null;

  const refresh = useCallback(async () => {
    if (!state.isAuthenticated) {
      setPermissions([]);
      setIsSuperAdmin(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.get<{ is_super_admin: boolean; permissions: Permission[] }>(
        '/api/v1/usr/permissions/me',
      );
      setIsSuperAdmin(!!res.data?.is_super_admin);
      setPermissions(res.data?.permissions || []);
    } catch (err) {
      console.warn('Failed to load permissions:', err);
      setIsSuperAdmin(false);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [state.isAuthenticated]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const check = useCallback(
    (activity: string | number, required: Extent): PermissionResult => {
      if (isSuperAdmin) return { allowed: true, message: 'Super admin', extent: 'view+modify+approve' };
      const match = matchActivity(permissions, activity);
      if (!match) {
        return { allowed: false, message: `You do not have access to '${activity}'.`, extent: null };
      }
      if (levelOf(match.extent) >= EXTENT_LEVELS[required]) {
        return { allowed: true, message: 'OK', extent: match.extent };
      }
      return {
        allowed: false,
        message: `Your role grants '${match.extent}'. This action needs '${required}'.`,
        extent: match.extent,
      };
    },
    [permissions, isSuperAdmin],
  );

  const can = useCallback(
    (activity: string | number, action: WorkflowAction, record?: RecordLike | null): PermissionResult => {
      if (isSuperAdmin) return { allowed: true, message: 'Super admin', extent: 'view+modify+approve' };
      const rule = ACTION_RULES[action];
      if (!rule) return { allowed: false, message: `Unknown action '${action}'.` };

      const match = matchActivity(permissions, activity);
      const extent = match?.extent ?? null;

      // 1. extent gate
      if (levelOf(extent) < EXTENT_LEVELS[rule.extent]) {
        return { allowed: false, extent, message: `This action needs '${rule.extent}'. Your role grants '${extent || 'no access'}'.` };
      }
      // 2. state gate
      const stateVal = record?.workflow_state || 'draft';
      if (rule.states && !rule.states.has(stateVal)) {
        return { allowed: false, extent, message: `'${action}' not allowed while record is '${stateVal}'.` };
      }
      // 3. ownership gate
      if (rule.owner && record) {
        const createdBy = record.created_by ?? null;
        const approvedBy = record.approved_by ?? null;
        if (rule.owner === 'creator' && createdBy !== userId) {
          return { allowed: false, extent, message: 'Only the creator may perform this action.' };
        }
        if (rule.owner === 'approver' && approvedBy !== userId) {
          return { allowed: false, extent, message: 'Only the approver may perform this action.' };
        }
        if (rule.owner === 'not_creator' && createdBy === userId) {
          return { allowed: false, extent, message: 'You cannot approve/reject your own record.' };
        }
        if (rule.owner === 'state') {
          if (PRE_APPROVAL.has(stateVal)) {
            if (createdBy !== userId) return { allowed: false, extent, message: 'Only the creator may edit a pre-approval record.' };
          } else if (approvedBy !== userId) {
            return { allowed: false, extent, message: 'Only the approver may edit an approved record.' };
          }
        }
      }
      return { allowed: true, message: 'OK', extent };
    },
    [permissions, isSuperAdmin, userId],
  );

  const value = useMemo(
    () => ({ loading, isSuperAdmin, userId, permissions, check, can, refresh }),
    [loading, isSuperAdmin, userId, permissions, check, can, refresh],
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

export const usePermissions = (): PermissionContextType => {
  const ctx = useContext(PermissionContext);
  if (!ctx) throw new Error('usePermissions must be used inside PermissionProvider');
  return ctx;
};

/** Extent-only check for one (activity, scope). */
export const usePermission = (activity: string | number, required: Extent): PermissionResult & { loading: boolean } => {
  const { check, loading } = usePermissions();
  return { ...check(activity, required), loading };
};

/** Shared user-facing message when an action is disabled for lack of permission.
 *  Surfaced as a tooltip on greyed action buttons + a toast on attempted click. */
export const NOT_AUTHORISED_MSG = 'You are not authorised — contact admin.';

/** Per-action button states for one record (the workflow engine).
 *  Pass `null`/omit `record` for create-context. */
export const useRecordPermissions = (
  activityKey: string | number,
  record?: RecordLike | null,
): Record<WorkflowAction, boolean> & { loading: boolean } => {
  const { can, loading } = usePermissions();
  const a = (action: WorkflowAction) => can(activityKey, action, record).allowed;
  return {
    loading,
    view: a('view'),
    report: a('report'),
    create: a('create'),
    edit: a('edit'),
    delete: a('delete'),
    submit: a('submit'),
    approve: a('approve'),
    reject: a('reject'),
    resubmit: a('resubmit'),
    deactivate: a('deactivate'),
  };
};
